<template>
  <div class="proposals-view">
    <!-- LoadingSpinner alleen bij initial load zonder search -->
    <LoadingSpinner
      v-if="loading && formattedProposals.length === 0 && !searchQuery && !hasLoadedOnce"
    />

    <!-- Table container with everything inside -->
    <div class="table-container table-container--hide-checkbox-mobile">
      <div class="table-heading">
        <div class="table-heading__title">
          {{ $t('proposals.title') }}
          <span class="table-heading__count">{{ pagination?.total || 0 }}</span>
        </div>
        <div class="table-heading__actions">
          <Btn
            variant="tertiary"
            icon="insights"
            :class="{ 'stats-toggle--active': showStats }"
            class="proposals-view__stats-toggle"
            @click="toggleStats"
          >
            {{ $t('proposals.stats_toggle') }}
          </Btn>
          <DropdownMenu class="status-filter">
            <template #trigger>
              <Btn variant="tertiary" icon="filter" :class="{ 'filter-active': hasActiveFilter }">
                {{ $t('proposals.filter.status') }}
                <span v-if="hasActiveFilter" class="filter-count">
                  {{ selectedStatuses.length }}
                </span>
              </Btn>
            </template>
            <template #items>
              <div class="status-filter__header">
                <span>{{ $t('proposals.filter.filter_by_status') }}</span>
                <button class="status-filter__reset" @click="resetStatusFilter">
                  {{ $t('proposals.filter.reset') }}
                </button>
              </div>
              <label v-for="status in allStatuses" :key="status" class="status-filter__option">
                <input
                  type="checkbox"
                  :value="status"
                  v-model="selectedStatuses"
                  @change="applyStatusFilter"
                />
                <ProposalStatusBadge :status="status" size="small" />
              </label>
            </template>
          </DropdownMenu>
          <Btn v-if="selectedItems.length > 0" icon="trash" @click="bulkDelete" variant="danger">
            {{ $t('proposals.delete_selected', { count: deletableSelectedCount }) }}
          </Btn>
          <Btn
            v-if="selectedItems.length > 0"
            icon="paper-plane"
            @click="bulkSend"
            variant="primary"
          >
            {{ $t('proposals.send_selected', { count: sendableSelectedCount }) }}
          </Btn>
          <SplitBtn
            v-if="!isMobile"
            icon="plus"
            variant="secondary"
            :menu-items="formMenuItems"
            @click="createManualProposal"
            @menu-item-click="openForm"
            class="proposals-view__add-proposal"
          >
            {{ $t('proposals.add_proposal') }}
          </SplitBtn>
          <Btn
            v-else
            icon="plus"
            @click="handleMobileAddProposal"
            variant="secondary"
            class="proposals-view__add-proposal"
            :title="$t('proposals.add_proposal')"
          />
        </div>
      </div>

      <!-- Stats bar (collapsible, inside table container) -->
      <ProposalStats v-if="showStats" class="proposals-view__stats" />

      <div class="table-search">
        <TextInput
          v-model="searchQuery"
          :placeholder="$t('proposals.search_placeholder')"
          icon="search"
          @input="handleSearch"
          class="proposals-view__search-input"
        />
      </div>

      <!-- Mobile Card View -->
      <div class="proposals-mobile-list" ref="mobileListRef">
        <!-- Skeleton loading for initial load or search -->
        <template v-if="loading && !isLoadingMore">
          <div v-for="i in 5" :key="`skeleton-${i}`" class="proposal-card proposal-card--skeleton">
            <Skeleton variant="rect" width="100%" height="80px" />
          </div>
        </template>

        <!-- Actual proposals -->
        <template v-else-if="formattedProposals.length > 0">
          <div
            v-for="proposal in formattedProposals"
            :key="proposal.id"
            class="proposal-card"
            @click="handleMobileCardClick(proposal)"
          >
            <div class="proposal-card__left">
              <div class="proposal-card__primary">
                <span class="proposal-card__number">{{ proposal.number }}</span>
                <span class="proposal-card__title">{{ proposal.title }}</span>
              </div>
              <div class="proposal-card__secondary">
                <span class="proposal-card__date">{{ proposal.createdAtShort }}</span>
                <span class="proposal-card__contact">{{ proposal.contact }}</span>
              </div>
            </div>
            <div class="proposal-card__right">
              <span class="proposal-card__amount">{{ proposal.amount }}</span>
              <ProposalStatusBadge :status="proposal.status" size="small" />
            </div>
          </div>
        </template>

        <!-- Infinite scroll loading indicator -->
        <div v-if="isLoadingMore" class="proposals-mobile-list__loading">
          <div
            v-for="i in 3"
            :key="`skeleton-more-${i}`"
            class="proposal-card proposal-card--skeleton"
          >
            <Skeleton variant="rect" width="100%" height="80px" />
          </div>
        </div>
        <div
          v-if="!hasMore && formattedProposals.length > 0 && !loading"
          class="proposals-mobile-list__end"
        >
          {{ $t('proposals.no_more_results') }}
        </div>
      </div>

      <!-- Mobile Action Sheet -->
      <ActionSheet
        v-model="showActionSheet"
        :title="selectedProposal?.title"
        :subtitle="selectedProposal?.contact"
        :actions="actionSheetActions"
        :cancel-text="$t('common.cancel')"
        @action="handleActionSheetAction"
      />

      <ActionSheet
        v-model="showCreateProposalActionSheet"
        :title="$t('proposals.add_proposal')"
        :actions="createProposalActionSheetItems"
        :cancel-text="$t('common.cancel')"
        @action="handleCreateProposalAction"
      />

      <!-- Desktop Table View - Always visible -->
      <EasyDataTable
        class="proposals-desktop-table"
        :headers="headers"
        :items="formattedProposals"
        :loading="loading && !isLoadingMore"
        :server-items-length="pagination?.total || 0"
        v-model:server-options="serverOptions"
        v-model:items-selected="selectedItems"
        :buttons-pagination="true"
        @click-row="handleRowClick"
        table-class-name="custom-table"
      >
        <!-- Empty state slot -->
        <template #empty>
          <div class="proposals-table-empty">
            <EmptyState
              :title="
                searchQuery ? $t('proposals.search_no_results') : $t('proposals.empty_state.title')
              "
              :description="
                searchQuery
                  ? $t('proposals.search_no_results_description')
                  : $t('proposals.empty_state.description')
              "
              icon="duotone-folders"
            >
              <SplitBtn
                v-if="!searchQuery && !isMobile"
                icon="plus"
                variant="secondary"
                :menu-items="formMenuItems"
                @click="createManualProposal"
                @menu-item-click="openForm"
              >
                {{ $t('proposals.add_proposal') }}
              </SplitBtn>
              <Btn
                v-else-if="!searchQuery"
                icon="plus"
                variant="secondary"
                @click="handleMobileAddProposal"
                class="proposals-view__add-proposal"
                :title="$t('proposals.add_proposal')"
              >
                {{ $t('proposals.add_proposal') }}
              </Btn>
            </EmptyState>
          </div>
        </template>
        <template #item-title="item">
          <TableRowLink
            v-if="isProposalRowLinked(item)"
            :to="proposalRoute(item)"
            :aria-label="`${$t('proposals.view')} ${item.title}`"
          >
            <span class="proposals-view__title-cell">
              <span>{{ item.title }}</span>
              <ProposalTypeBadge v-if="isMeasurementWorkflow" :proposal-type="item.proposalType" />
            </span>
          </TableRowLink>
          <div v-else class="proposals-view__title-cell">
            <span>{{ item.title }}</span>
            <ProposalTypeBadge v-if="isMeasurementWorkflow" :proposal-type="item.proposalType" />
          </div>
        </template>
        <template #item-status="item">
          <ProposalStatusBadge :status="item.status" />
        </template>
        <template #item-actions="item">
          <div class="actions" @click.stop>
            <!-- Draft: edit actions -->
            <SplitBtn
              v-if="item.status === 'draft'"
              variant="tertiary"
              :menu-items="[
                { label: $t('proposals.edit'), value: 'edit' },
                { label: $t('proposals.duplicate'), value: 'duplicate' },
                { label: $t('proposals.send'), value: 'send' },
                { label: $t('proposals.delete'), value: 'delete' },
              ]"
              @click="handleAction('edit', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.edit') }}
            </SplitBtn>
            <!-- Sent/Viewed: view actions with withdraw/reject option -->
            <SplitBtn
              v-else-if="item.status === 'sent' || item.status === 'viewed'"
              variant="tertiary"
              :menu-items="[
                { label: $t('proposals.view'), value: 'view' },
                { label: $t('proposals.edit_anyway'), value: 'edit_anyway' },
                { label: $t('proposals.resend'), value: 'resend' },
                { label: $t('proposals.duplicate'), value: 'duplicate' },
                { label: $t('proposals.mark_accepted'), value: 'mark_accepted' },
                { label: $t('proposals.mark_rejected'), value: 'mark_rejected', danger: true },
                { label: $t('proposals.reject'), value: 'reject', danger: true },
                { label: $t('proposals.withdraw'), value: 'withdraw', danger: true },
              ]"
              @click="handleAction('view', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.view') }}
            </SplitBtn>
            <!-- Rejected/Withdrawn: view with restore option -->
            <SplitBtn
              v-else-if="item.status === 'rejected' || item.status === 'withdrawn'"
              variant="tertiary"
              :menu-items="[
                { label: $t('proposals.view'), value: 'view' },
                { label: $t('proposals.duplicate'), value: 'duplicate' },
                { label: $t('proposals.restore'), value: 'restore' },
              ]"
              @click="handleAction('view', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.view') }}
            </SplitBtn>
            <!-- Accepted: view with edit option -->
            <SplitBtn
              v-else-if="item.status === 'accepted'"
              variant="tertiary"
              :menu-items="[
                { label: $t('proposals.view'), value: 'view' },
                { label: $t('proposals.edit_anyway'), value: 'edit_anyway' },
                { label: $t('proposals.duplicate'), value: 'duplicate' },
              ]"
              @click="handleAction('view', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.view') }}
            </SplitBtn>
            <!-- Other final statuses (superseded): view only -->
            <SplitBtn
              v-else
              variant="tertiary"
              :menu-items="[
                { label: $t('proposals.view'), value: 'view' },
                { label: $t('proposals.duplicate'), value: 'duplicate' },
              ]"
              @click="handleAction('view', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.view') }}
            </SplitBtn>
          </div>
        </template>
      </EasyDataTable>
    </div>
    <!-- Sentinel element for intersection observer (works for both mobile and desktop) -->
    <div ref="sentinelRef" class="proposals-sentinel">
      <!-- Loading indicator when loading more -->
      <div v-if="isLoadingMore" class="proposals-sentinel__loading">
        <LoadingSpinner />
      </div>
    </div>
  </div>
</template>

<script>
  import {
    defineComponent,
    onMounted,
    onUnmounted,
    ref,
    computed,
    watch,
    getCurrentInstance,
    nextTick,
  } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useUrlPagination } from '@/composables/useUrlPagination';
  import { crmService } from '@/services/crmService';
  import {
    openSalesOrderNotice,
    salesOrderBlockFromError,
    salesOrderRevertPreview,
  } from '@/utils/salesOrderNotice';
  import { formService } from '@/services/formService';
  import { useCrmStore } from '@/store/crmStore';
  import { useToastStore } from '@/store/toastStore';
  import { useUIStore } from '@/store/uiStore';
  import { useConfetti } from '@/services/confettiService';
  import { useUserStore } from '@/store/userStore';

  import { formatDate, formatDateShort } from '@/utils/date';
  import { getRejectReasonModalProps } from '@/utils/proposalRejectReason';
  import { formatProposalNumber } from '@shared/utils/proposalNumbering';
  import { useOpenProposalConfigurator } from '@/composables/useOpenProposalConfigurator';

  import EasyDataTable from 'vue3-easy-data-table';
  import 'vue3-easy-data-table/dist/style.css';

  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import Btn from '@/components/partials/Btn.vue';
  import SplitBtn from '@/components/partials/SplitBtn.vue';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import DropdownMenu from '@/components/partials/DropdownMenu.vue';
  import ActionSheet from '@/components/partials/ActionSheet.vue';
  import TableRowLink from '@/components/tables/TableRowLink.vue';
  import ProposalStatusBadge from '@/components/partials/ProposalStatusBadge.vue';
  import ProposalTypeBadge from '@/components/partials/ProposalTypeBadge.vue';
  import { PROPOSAL_WORKFLOW } from '@/constants/proposalTypes';
  import TextInput from '@/components/inputs/TextInput.vue';
  import Skeleton from '@shared/components/Skeleton.vue';
  import ProposalStats from '@/components/crm/ProposalStats.vue';

  export default defineComponent({
    name: 'LeadProposals',

    components: {
      EasyDataTable,
      LoadingSpinner,
      EmptyState,
      Btn,
      PageHeader,
      SplitBtn,
      DropdownMenu,
      ActionSheet,
      TableRowLink,
      ProposalStatusBadge,
      ProposalTypeBadge,
      TextInput,
      Skeleton,
      ProposalStats,
    },

    props: {
      leadId: {
        type: String,
        required: false,
        default: null,
      },
    },

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const crmStore = useCrmStore();
      const userStore = useUserStore();
      const loading = ref(true);
      const configuratorForms = ref([]);

      const isMeasurementWorkflow = computed(() => {
        return (
          userStore.currentOrganization?.settings?.proposal_workflow ===
          PROPOSAL_WORKFLOW.MEASUREMENT
        );
      });

      // Stats toggle with localStorage persistence
      const STATS_STORAGE_KEY = 'smartflow_proposals_show_stats';
      const showStats = ref(localStorage.getItem(STATS_STORAGE_KEY) !== 'false');
      const toggleStats = () => {
        showStats.value = !showStats.value;
        localStorage.setItem(STATS_STORAGE_KEY, String(showStats.value));
      };
      const hasLoadedOnce = ref(false);
      const toastStore = useToastStore();
      const confetti = useConfetti();
      const proposals = computed(() => crmStore.proposals);
      const pagination = computed(() => crmStore.proposalPagination);

      const route = useRoute();
      const router = useRouter();
      const { serverOptions } = useUrlPagination(20);

      const selectedItems = ref([]);

      // Search
      const searchQuery = ref('');
      const searchTimeout = ref(null);

      // Infinite scroll (only for mobile)
      const mobileListRef = ref(null);
      const sentinelRef = ref(null);
      const isLoadingMore = ref(false);
      const currentPage = ref(1);
      const isAlive = ref(true);
      const hasMore = computed(() => {
        const total = pagination.value?.total || 0;
        return proposals.value.length < total;
      });

      // Detect mobile breakpoint (lg-down)
      const isMobile = ref(false);
      const updateIsMobile = () => {
        if (typeof window === 'undefined') return;
        isMobile.value = window.innerWidth < 1024;
        logCreateProposalUIState('updateIsMobile');
      };

      // Intersection Observer for lazy loading (mobile only)
      let intersectionObserver = null;

      // Status filter — last choice is kept across refresh and later visits
      const STATUS_FILTER_STORAGE_KEY = 'smartflow_proposals_status_filter';
      const allStatuses = [
        'draft',
        'sending',
        'sent',
        'viewed',
        'accepted',
        'rejected',
        'superseded',
        'withdrawn',
      ];
      const defaultStatuses = ['draft', 'sending', 'sent', 'viewed', 'accepted', 'rejected'];

      const readStoredStatuses = () => {
        try {
          const raw = localStorage.getItem(STATUS_FILTER_STORAGE_KEY);
          if (raw == null) return null;
          const parsed = JSON.parse(raw);
          if (!Array.isArray(parsed)) return null;
          const valid = parsed.filter((status) => allStatuses.includes(status));
          if (valid.length === 0 && parsed.length > 0) return null;
          return valid;
        } catch {
          return null;
        }
      };

      const selectedStatuses = ref(readStoredStatuses() ?? [...defaultStatuses]);

      const hasActiveFilter = computed(() => {
        // Filter is "active" if not all statuses are selected or if selection differs from default
        return selectedStatuses.value.length !== allStatuses.length;
      });

      const persistStatusFilter = () => {
        localStorage.setItem(STATUS_FILTER_STORAGE_KEY, JSON.stringify(selectedStatuses.value));
        // Drop a deep-link ?status= so refresh uses the saved choice
        if (route.query.status) {
          const query = { ...route.query };
          delete query.status;
          router.replace({ query });
        }
      };

      const applyStatusFilter = () => {
        persistStatusFilter();
        currentPage.value = 1;
        loadProposals(1, serverOptions.value.rowsPerPage);
      };

      const resetStatusFilter = () => {
        selectedStatuses.value = [...allStatuses];
        applyStatusFilter();
      };

      // Compute how many selected items can be deleted (only drafts)
      const deletableSelectedCount = computed(() => {
        return selectedItems.value.filter((item) => item.status === 'draft').length;
      });

      const sendableSelectedItems = computed(() => {
        return selectedItems.value.filter((item) => item.status === 'draft');
      });

      const sendableSelectedCount = computed(() => sendableSelectedItems.value.length);

      const headers = [
        { text: '', value: 'number' },
        { text: $t('proposals.title_field'), value: 'title' },
        { text: $t('proposals.status'), value: 'status' },
        { text: $t('proposals.total_amount'), value: 'amount' },
        { text: $t('proposals.contact'), value: 'contact' },
        { text: $t('proposals.created_at'), value: 'createdAt' },
        { text: $t('proposals.valid_until'), value: 'validUntil' },
        { text: $t('proposals.actions'), value: 'actions' },
      ];

      // Helper: Get netto value (excl. BTW) with backward compatibility
      const getNettoValue = (proposal) => {
        const total = proposal.total_amount || 0;

        // If prices_include_vat is false, total_amount is already excl. BTW
        if (proposal.prices_include_vat === false) {
          return total;
        }

        // If prices_include_vat is true (or undefined for backward compat),
        // use subtotal_excl_vat if available, otherwise calculate
        if (proposal.subtotal_excl_vat != null) {
          return proposal.subtotal_excl_vat;
        }

        // Fallback: calculate from total_amount using vat_rate (default 21%)
        const vatRate = proposal.vat_rate ?? 21;
        return Math.round((total / (1 + vatRate / 100)) * 100) / 100;
      };

      const formattedProposals = computed(() => {
        // Return empty array during loading to show skeleton
        if (loading.value && !isLoadingMore.value) {
          return [];
        }

        const organization = userStore.currentOrganization
          ? {
              settings: userStore.currentOrganization.settings || {},
            }
          : undefined;

        return proposals.value.map((proposal) => {
          // Format proposal number using organization settings
          const formattedNumber =
            formatProposalNumber(proposal, organization) || proposal.proposal_number;

          return {
            id: proposal.id,
            createdAt: formatDate(proposal.created_at),
            createdAtShort: formatDateShort(proposal.created_at),
            title: proposal.title,
            status: proposal.status,
            proposalType: proposal.proposal_type || 'definitive',
            number: formattedNumber,
            contact: proposal.lead?.name,
            leadId: proposal.lead?.id,
            leadPhone: proposal.lead?.phone,
            // Use netto value (excl. BTW) for display
            amount: new Intl.NumberFormat('nl-NL', {
              style: 'currency',
              currency: proposal.currency || 'EUR',
            }).format(proposal.subtotal_excl_vat || getNettoValue(proposal)),
            validUntil: formatDateShort(proposal.valid_until),
          };
        });
      });

      // Mobile action sheet state
      const showActionSheet = ref(false);
      const selectedProposal = ref(null);
      const showCreateProposalActionSheet = ref(false);

      const { openProposalConfigurator } = useOpenProposalConfigurator();

      const formMenuItems = computed(() => {
        return configuratorForms.value.map((form) => ({
          label: form.name,
          value: form.id,
        }));
      });

      const createProposalActionSheetItems = computed(() => {
        return [
          { label: $t('proposals.create_new_proposal'), value: 'manual' },
          ...configuratorForms.value.map((form) => ({
            label: form.name,
            value: form.id,
          })),
        ];
      });

      const logCreateProposalUIState = (source) => {
        const mode =
          configuratorForms.value.length > 0
            ? isMobile.value
              ? 'mobile-plus-button'
              : 'desktop-split-button'
            : 'disabled-no-forms';
      };

      // Get actions for a proposal based on status
      const getActionsForProposal = (proposal) => {
        if (!proposal) return [];

        if (proposal.status === 'draft') {
          return [
            { label: $t('proposals.edit'), value: 'edit' },
            { label: $t('proposals.send'), value: 'send' },
            { label: $t('proposals.duplicate'), value: 'duplicate' },
            { label: $t('proposals.delete'), value: 'delete', danger: true },
          ];
        } else if (proposal.status === 'sent' || proposal.status === 'viewed') {
          return [
            { label: $t('proposals.view'), value: 'view' },
            { label: $t('proposals.edit_anyway'), value: 'edit_anyway' },
            { label: $t('proposals.resend'), value: 'resend' },
            { label: $t('proposals.duplicate'), value: 'duplicate' },
            { label: $t('proposals.mark_accepted'), value: 'mark_accepted' },
            { label: $t('proposals.mark_rejected'), value: 'mark_rejected', danger: true },
            { label: $t('proposals.reject'), value: 'reject', danger: true },
            { label: $t('proposals.withdraw'), value: 'withdraw', danger: true },
          ];
        } else if (proposal.status === 'rejected' || proposal.status === 'withdrawn') {
          // Rejected/withdrawn: can be restored
          return [
            { label: $t('proposals.view'), value: 'view' },
            { label: $t('proposals.duplicate'), value: 'duplicate' },
            { label: $t('proposals.restore'), value: 'restore' },
          ];
        } else if (proposal.status === 'accepted') {
          // accepted: can be edited
          return [
            { label: $t('proposals.view'), value: 'view' },
            { label: $t('proposals.edit_anyway'), value: 'edit_anyway' },
            { label: $t('proposals.duplicate'), value: 'duplicate' },
          ];
        } else {
          // superseded
          return [
            { label: $t('proposals.view'), value: 'view' },
            { label: $t('proposals.duplicate'), value: 'duplicate' },
          ];
        }
      };

      const actionSheetActions = computed(() => getActionsForProposal(selectedProposal.value));

      // Handle mobile card click - show action sheet
      const handleMobileCardClick = (proposal) => {
        selectedProposal.value = proposal;
        showActionSheet.value = true;
      };

      // Handle action sheet action
      const handleActionSheetAction = (actionValue) => {
        if (selectedProposal.value) {
          handleAction(actionValue, selectedProposal.value);
        }
      };

      const handleRowClick = (row) => {
        // Don't open superseded or withdrawn proposals
        if (row.status === 'superseded' || row.status === 'withdrawn') {
          return;
        }
        if (row.status === 'draft') {
          editProposal(row);
        } else {
          viewProposal(row);
        }
      };

      const isProposalRowLinked = (proposal) => {
        return proposal.status !== 'superseded' && proposal.status !== 'withdrawn';
      };

      const proposalRoute = (proposal) => ({
        name: 'ProposalDetail',
        params: { id: proposal.id },
      });

      const handleAction = (action, item) => {
        if (action === 'edit') {
          editProposal(item);
        } else if (action === 'edit_anyway') {
          editProposalAnyway(item);
        } else if (action === 'duplicate') {
          duplicateProposal(item);
        } else if (action === 'delete') {
          deleteProposal(item);
        } else if (action === 'withdraw') {
          withdrawProposal(item);
        } else if (action === 'reject') {
          rejectProposal(item);
        } else if (action === 'restore') {
          restoreProposal(item);
        } else if (action === 'mark_accepted') {
          markProposalAsAccepted(item);
        } else if (action === 'mark_rejected') {
          markProposalAsRejected(item);
        } else if (action === 'send' || action === 'view' || action === 'resend') {
          viewProposal(item);
        }
      };

      // Methods for pagination
      const handlePageChange = async (settings) => {
        await loadProposals(settings.page, settings.limit);
      };

      const loadProposals = async (page = 1, limit = 50, append = false) => {
        if (append) {
          isLoadingMore.value = true;
        } else {
          loading.value = true;
        }
        try {
          const params = { page, limit };
          // Add status filter if not all statuses are selected
          if (
            selectedStatuses.value.length > 0 &&
            selectedStatuses.value.length < allStatuses.length
          ) {
            params.status = selectedStatuses.value.join(',');
          }
          // Add search query if present
          if (searchQuery.value.trim()) {
            params.search = searchQuery.value.trim();
          }
          await crmStore.fetchProposals(params, append);
          hasLoadedOnce.value = true;
        } catch (error) {
          console.error('Failed to load proposals:', error);
        } finally {
          loading.value = false;
          isLoadingMore.value = false;
        }
      };

      // Handle search with debounce
      const handleSearch = () => {
        if (searchTimeout.value) {
          clearTimeout(searchTimeout.value);
        }
        searchTimeout.value = setTimeout(() => {
          currentPage.value = 1;
          loadProposals(1, serverOptions.value.rowsPerPage);
        }, 300);
      };

      // Handle intersection observer callback for lazy loading (mobile only)
      const handleIntersection = (entries) => {
        const entry = entries[0];

        // Only trigger on mobile
        if (
          isMobile.value &&
          entry.isIntersecting &&
          hasMore.value &&
          !isLoadingMore.value &&
          !loading.value
        ) {
          currentPage.value += 1;
          loadProposals(currentPage.value, serverOptions.value.rowsPerPage, true);
        }
      };

      // Resize handler (defined here so it can be removed in onUnmounted)
      let handleResize = null;

      // Watch for changes in serverOptions and trigger pagination (desktop only)
      watch(
        serverOptions,
        (newOptions) => {
          // Only handle pagination on desktop (not mobile with lazy loading)
          if (!isMobile.value) {
            handlePageChange({
              page: newOptions.page,
              limit: newOptions.rowsPerPage,
            });
          }
        },
        { deep: true }
      );

      const uiStore = useUIStore();

      const fetchConfiguratorForms = async () => {
        try {
          const response = await formService.getForms();
          const forms = response.forms || response || [];
          configuratorForms.value = forms.filter((form) => form.type === 'configurator');
          console.log('[ProposalsView:create-proposal-ui] forms loaded', {
            totalForms: forms.length,
            configuratorForms: configuratorForms.value.map((form) => ({
              id: form.id,
              name: form.name,
            })),
          });
          logCreateProposalUIState('fetchConfiguratorForms');
        } catch (error) {
          console.error('Failed to fetch forms:', error);
        }
      };

      const openForm = (formId) => {
        if (!formId || formId === 'manual') return;
        const returnTo =
          typeof window !== 'undefined' ? `${window.location.origin}/proposals` : null;
        openProposalConfigurator(
          {
            formId,
            leadId: props.leadId || null,
            returnTo,
          },
          $t
        );
      };

      const createManualProposal = () => {
        router.push({ name: 'ProposalCreate' });
      };

      const handleMobileAddProposal = () => {
        if (configuratorForms.value.length > 0) {
          showCreateProposalActionSheet.value = true;
        } else {
          createManualProposal();
        }
      };

      const handleCreateProposalAction = (value) => {
        if (value === 'manual') {
          createManualProposal();
        } else {
          openForm(value);
        }
      };

      const editProposal = (proposal) => {
        router.push({ name: 'ProposalDetail', params: { id: proposal.id } });
      };

      const editProposalAnyway = (proposal) => {
        router.push({ name: 'ProposalDetail', params: { id: proposal.id } });
      };

      const duplicateProposal = async (proposal) => {
        const { default: DuplicateProposalModal } = await import(
          '@/components/crm/proposal/DuplicateProposalModal.vue'
        );
        const modalId = uiStore.addModal(DuplicateProposalModal, {
          title: $t('proposals.duplicate_proposal'),
          proposalId: proposal.id,
          currentLeadId: props.leadId || proposal.leadId || null,
          onSubmit: (newProposal) => {
            uiStore.closeModal(modalId);
            toastStore.addToast({
              type: 'success',
              message: $t('proposals.proposal_duplicated'),
            });
            router.push({ name: 'ProposalDetail', params: { id: newProposal.id } });
          },
          onCancel: () => uiStore.closeModal(modalId),
        });
      };

      const viewProposal = (proposal) => {
        router.push({ name: 'ProposalDetail', params: { id: proposal.id } });
      };

      const deleteProposal = async (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.delete_proposal'),
          description: $t('proposals.delete_proposal_confirmation'),
          status: 'danger',
          onSubmit: async () => {
            try {
              const leadId = props.leadId || proposal.leadId;
              await crmService.deleteProposal(leadId, proposal.id);
              proposals.value = proposals.value.filter((p) => p.id !== proposal.id);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.proposal_deleted'),
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error deleting proposal:', error);
              const errorMessage = error.response?.data?.error || $t('proposals.error_deleting');
              toastStore.addToast({
                message: errorMessage,
                type: 'error',
                duration: 5000,
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      const withdrawProposal = async (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.withdraw_proposal'),
          description: $t('proposals.withdraw_proposal_confirmation'),
          status: 'warning',
          showReasonInput: true,
          reasonLabel: $t('proposals.withdraw_reason_label'),
          reasonPlaceholder: $t('proposals.withdraw_reason_placeholder'),
          onSubmit: async (reason) => {
            try {
              const leadId = props.leadId || proposal.leadId;
              const updatedProposal = await crmService.withdrawProposal(
                leadId,
                proposal.id,
                reason || null
              );
              // Update proposal in state
              const index = proposals.value.findIndex((p) => p.id === proposal.id);
              if (index !== -1) {
                proposals.value[index] = updatedProposal;
              }
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.proposal_withdrawn'),
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error withdrawing proposal:', error);
              const errorMessage = error.response?.data?.error || $t('proposals.error_withdrawing');
              toastStore.addToast({
                message: errorMessage,
                type: 'error',
                duration: 5000,
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      const rejectProposal = async (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.reject_proposal'),
          description: $t('proposals.reject_proposal_confirmation'),
          status: 'warning',
          ...getRejectReasonModalProps($t),
          onSubmit: async (reason, { reasonCode } = {}) => {
            try {
              const leadId = props.leadId || proposal.leadId;
              const updatedProposal = await crmService.rejectProposal(
                leadId,
                proposal.id,
                reason || null,
                reasonCode || null
              );
              // Update proposal in state
              const index = proposals.value.findIndex((p) => p.id === proposal.id);
              if (index !== -1) {
                proposals.value[index] = updatedProposal;
              }
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.proposal_rejected'),
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error rejecting proposal:', error);
              const errorMessage = error.response?.data?.error || $t('proposals.error_rejecting');
              toastStore.addToast({
                message: errorMessage,
                type: 'error',
                duration: 5000,
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      const restoreProposal = async (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.restore_proposal'),
          description: $t('proposals.restore_proposal_confirmation'),
          status: 'info',
          onSubmit: async () => {
            try {
              const leadId = props.leadId || proposal.leadId;
              const updatedProposal = await crmService.restoreProposal(leadId, proposal.id);
              // Update proposal in state
              const index = proposals.value.findIndex((p) => p.id === proposal.id);
              if (index !== -1) {
                proposals.value[index] = updatedProposal;
              }
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.proposal_restored'),
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error restoring proposal:', error);
              const errorMessage = error.response?.data?.error || $t('proposals.error_restoring');
              toastStore.addToast({
                message: errorMessage,
                type: 'error',
                duration: 5000,
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      const markProposalAsAccepted = async (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.mark_accepted_title'),
          description: $t('proposals.mark_accepted_confirmation'),
          status: 'success',
          onSubmit: async () => {
            uiStore.updateModalProps(modalId, { loading: true });
            try {
              const leadId = props.leadId || proposal.leadId;
              const updatedProposal = await crmService.markProposalAsAccepted(
                leadId,
                proposal.id,
                null
              );
              // Update proposal in state
              const index = proposals.value.findIndex((p) => p.id === proposal.id);
              if (index !== -1) {
                proposals.value[index] = updatedProposal;
              }
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.proposal_marked_accepted'),
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error marking proposal as accepted:', error);
              uiStore.updateModalProps(modalId, { loading: false });
              toastStore.addToast({
                message: $t('proposals.error_marking_accepted'),
                type: 'error',
              });
            }
          },
        });
      };

      const markProposalAsRejected = async (proposal) => {
        const preview =
          proposal.status === 'accepted'
            ? await salesOrderRevertPreview(proposal.id, 'reject')
            : null;
        if (preview && !preview.allowed) {
          openSalesOrderNotice(uiStore, preview);
          return;
        }

        const modalId = uiStore.addModal(null, {
          title: preview?.title || $t('proposals.mark_rejected_title'),
          description: preview?.description || $t('proposals.mark_rejected_confirmation'),
          status: 'warning',
          ...getRejectReasonModalProps($t),
          onSubmit: async (reason, { reasonCode } = {}) => {
            uiStore.updateModalProps(modalId, { loading: true });
            try {
              const leadId = props.leadId || proposal.leadId;
              const updatedProposal = await crmService.markProposalAsRejected(
                leadId,
                proposal.id,
                reason || null,
                reasonCode || null
              );
              // Update proposal in state
              const index = proposals.value.findIndex((p) => p.id === proposal.id);
              if (index !== -1) {
                proposals.value[index] = updatedProposal;
              }
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.proposal_marked_rejected'),
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error marking proposal as rejected:', error);
              const blocked = salesOrderBlockFromError(error);
              uiStore.updateModalProps(modalId, { loading: false });
              if (blocked) {
                uiStore.closeModal(modalId);
                openSalesOrderNotice(uiStore, blocked);
                return;
              }
              toastStore.addToast({
                message: $t('proposals.error_marking_rejected'),
                type: 'error',
              });
            }
          },
        });
      };

      const bulkDelete = async () => {
        // Filter only draft proposals (only drafts can be deleted)
        const deletableItems = selectedItems.value.filter((item) => item.status === 'draft');

        if (deletableItems.length === 0) {
          toastStore.addToast({
            message: $t('proposals.bulk_delete_no_drafts'),
            type: 'warning',
            duration: 3000,
          });
          return;
        }

        const nonDeletableCount = selectedItems.value.length - deletableItems.length;
        let description = $t('proposals.bulk_delete_confirmation', {
          count: deletableItems.length,
        });
        if (nonDeletableCount > 0) {
          description += ' ' + $t('proposals.bulk_delete_skipped', { count: nonDeletableCount });
        }

        const modalId = uiStore.addModal(null, {
          title: $t('proposals.bulk_delete_title'),
          description: description,
          status: 'danger',
          onSubmit: async () => {
            try {
              const proposalIds = deletableItems.map((item) => item.id);
              // Use the leadId from the first deletable item
              const leadId = props.leadId || deletableItems[0]?.leadId;
              await crmService.bulkDeleteProposals(leadId, proposalIds);

              // Remove deleted proposals from the store
              const deletedIds = new Set(proposalIds);
              crmStore.proposals = crmStore.proposals.filter((p) => !deletedIds.has(p.id));

              // Clear selection
              selectedItems.value = [];

              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.bulk_delete_success', { count: deletableItems.length }),
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error bulk deleting proposals:', error);
              const errorMessage =
                error.response?.data?.error || $t('proposals.error_bulk_deleting');
              toastStore.addToast({
                message: errorMessage,
                type: 'error',
                duration: 5000,
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      const bulkSend = async () => {
        const sendableItems = sendableSelectedItems.value;

        if (sendableItems.length === 0) {
          toastStore.addToast({
            message: $t('proposals.bulk_send_no_drafts'),
            type: 'warning',
            duration: 3000,
          });
          return;
        }
        const nonSendableCount = selectedItems.value.length - sendableItems.length;
        let description = $t('proposals.bulk_send_confirmation', {
          count: sendableItems.length,
        });
        if (nonSendableCount > 0) {
          description += ' ' + $t('proposals.bulk_send_skipped', { count: nonSendableCount });
        }

        const modalId = uiStore.addModal(null, {
          title: $t('proposals.bulk_send_title'),
          description: description,
          onSubmit: async () => {
            try {
              const proposalIds = sendableItems.map((item) => item.id);
              const result = await crmService.bulkSendProposals(proposalIds);

              const queuedIds = new Set(result.queued.map((q) => q.id));
              crmStore.proposals = crmStore.proposals.map((p) =>
                queuedIds.has(p.id) ? { ...p, status: 'sending' } : p
              );

              selectedItems.value = [];
              uiStore.closeModal(modalId);

              toastStore.addToast({
                message: $t('proposals.bulk_send_success', { count: result.queued.length }),
                type: 'success',
                duration: 3000,
              });

              // Server-side skips carry a per-proposal reason; surface each
              // distinct reason instead of one catch-all. `has_successor` is
              // not a failure (a newer version exists), so it's shown as info.
              if (result.skipped.length > 0) {
                const idsByReason = result.skipped.reduce((acc, skip) => {
                  (acc[skip.reason] = acc[skip.reason] || []).push(skip.id);
                  return acc;
                }, {});
                const KNOWN_SKIP_REASONS = [
                  'not_draft',
                  'no_recipient',
                  'same_deal_conflict',
                  'has_successor',
                  'send_failed',
                ];
                Object.entries(idsByReason).forEach(([reason, ids]) => {
                  const reasonKey = KNOWN_SKIP_REASONS.includes(reason) ? reason : 'default';
                  toastStore.addToast({
                    message: $t(`proposals.bulk_send_skip_reason.${reasonKey}`, {
                      count: ids.length,
                    }),
                    type: reason === 'has_successor' ? 'info' : 'warning',
                    duration: 8000,
                  });
                });
              }

              // Poll until every queued send settles, then one closing refetch so
              // the table reflects real server state instead of staying stuck on
              // the optimistic 'sending' patch above.
              if (result.queued.length > 0) {
                crmService
                  .pollBulkSendStatus(result.queued.map((q) => q.id))
                  .then((settled) => {
                    if (!isAlive.value) return;
                    const outcomes = Array.from(settled.values());

                    if (outcomes.includes('sent')) {
                      confetti.fire();
                    }

                    const failedCount = outcomes.filter((s) => s === 'failed').length;
                    if (failedCount > 0) {
                      toastStore.addToast({
                        message: $t('proposals.bulk_send_failed', { count: failedCount }),
                        type: 'error',
                        duration: 8000,
                      });
                    }

                    const timedOutCount = outcomes.filter((s) => s === 'timed_out').length;
                    if (timedOutCount > 0) {
                      toastStore.addToast({
                        message: $t('proposals.bulk_send_taking_longer', {
                          count: timedOutCount,
                        }),
                        type: 'warning',
                        duration: 8000,
                      });
                    }
                  })
                  .finally(() => {
                    if (isAlive.value) {
                      loadProposals(currentPage.value, serverOptions.value.rowsPerPage);
                    }
                  });
              }
            } catch (error) {
              console.error('Error bulk sending proposals:', error);
              toastStore.addToast({
                message: $t('proposals.error_bulk_sending'),
                type: 'error',
                duration: 5000,
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      watch([isMobile, configuratorForms], () => {
        logCreateProposalUIState('watch:isMobile-or-forms');
      });

      onMounted(async () => {
        updateIsMobile();
        await fetchConfiguratorForms();

        // Reset pagination
        currentPage.value = 1;

        // Initialize status filter from query param (deep-link, e.g. from dashboard)
        const statusQuery = route.query.status;
        if (statusQuery) {
          const requested = String(statusQuery)
            .split(',')
            .map((s) => s.trim())
            .filter((s) => allStatuses.includes(s));
          if (requested.length > 0) {
            selectedStatuses.value = requested;
          }
        }

        // Load with default status filter
        const params = { page: 1, limit: serverOptions.value.rowsPerPage };
        if (selectedStatuses.value.length < allStatuses.length) {
          params.status = selectedStatuses.value.join(',');
        }
        await crmStore.fetchProposals(params);
        loading.value = false;

        // Setup Intersection Observer for lazy loading (mobile only)
        await nextTick();
        if (sentinelRef.value && isMobile.value) {
          intersectionObserver = new IntersectionObserver(handleIntersection, {
            root: null, // Use viewport
            rootMargin: '200px', // Start loading 200px before reaching the sentinel
            threshold: 0.1,
          });
          intersectionObserver.observe(sentinelRef.value);
        }

        // Watch for window resize to setup/teardown observer
        handleResize = () => {
          updateIsMobile();
          if (sentinelRef.value) {
            if (isMobile.value && !intersectionObserver) {
              // Setup observer on mobile
              intersectionObserver = new IntersectionObserver(handleIntersection, {
                root: null,
                rootMargin: '200px',
                threshold: 0.1,
              });
              intersectionObserver.observe(sentinelRef.value);
            } else if (!isMobile.value && intersectionObserver) {
              // Teardown observer on desktop
              intersectionObserver.unobserve(sentinelRef.value);
              intersectionObserver.disconnect();
              intersectionObserver = null;
            }
          }
        };
        window.addEventListener('resize', handleResize);

        // Redirect ?open= query param to the new proposal detail page
        const openProposalId = route.query.open;
        if (openProposalId) {
          router.replace({
            name: 'ProposalDetail',
            params: { id: openProposalId },
          });
        }
      });

      onUnmounted(() => {
        isAlive.value = false;
        // Cleanup Intersection Observer
        if (intersectionObserver && sentinelRef.value) {
          intersectionObserver.unobserve(sentinelRef.value);
          intersectionObserver.disconnect();
          intersectionObserver = null;
        }
        // Remove resize listener
        if (handleResize) {
          window.removeEventListener('resize', handleResize);
        }
      });

      return {
        formatDate,
        loading,
        proposals,
        pagination,
        serverOptions,
        selectedItems,
        deletableSelectedCount,
        sendableSelectedCount,
        createManualProposal,
        handleMobileAddProposal,
        handleCreateProposalAction,
        headers,
        formattedProposals,
        handleRowClick,
        isProposalRowLinked,
        proposalRoute,
        handleAction,
        handlePageChange,
        handleIntersection,
        duplicateProposal,
        bulkDelete,
        bulkSend,
        // Stats toggle
        showStats,
        toggleStats,
        isMeasurementWorkflow,
        // Status filter
        allStatuses,
        selectedStatuses,
        hasActiveFilter,
        applyStatusFilter,
        resetStatusFilter,
        // Mobile action sheet
        showActionSheet,
        selectedProposal,
        actionSheetActions,
        handleMobileCardClick,
        handleActionSheetAction,
        isMobile,
        showCreateProposalActionSheet,
        createProposalActionSheetItems,
        configuratorForms,
        formMenuItems,
        openForm,
        // Search
        searchQuery,
        handleSearch,
        // Infinite scroll
        mobileListRef,
        sentinelRef,
        isLoadingMore,
        hasMore,
      };
    },
  });
</script>

<style scoped lang="scss">
  // All table styling is now handled by the global _tables.scss
  // Only component-specific styles remain here

  .table-container__search {
    padding: 0.5rem 1rem;
  }

  .proposals-view {
    &__stats {
      padding: 0 1rem;
      margin-bottom: 0;
      :deep(.stats-bar) {
        margin: 1rem 0 0.5rem 0;
      }
    }

    &__stats-toggle {
      &.stats-toggle--active {
        background-color: var(--brand-50);
        color: var(--brand-600);
        border-color: var(--brand-200);
      }
      @include lg-down {
        display: none;
        visibility: hidden;
      }
    }

    &__add-proposal {
      @include lg-down {
        :deep(.btn__content) {
          span {
            display: none;
            visibility: hidden;
          }
          .icon {
            margin-left: 0;
          }
        }
        padding: 0.65rem;
      }
    }

    &__search {
      padding: 0.5rem 1rem;

      &-input {
        width: 100%;
      }
    }
  }
  .table-search {
    padding: 0.25rem 1rem;
  }

  // Mobile card list - only visible on mobile
  .proposals-mobile-list {
    display: none;

    @include md-down {
      display: block;
    }

    &__loading {
      padding: 0.5rem 0;
    }

    &__end {
      text-align: center;
      padding: 1.5rem;
      color: var(--slate-500);
      font-size: 0.875rem;
    }
  }

  // Desktop table - hidden on mobile
  .proposals-desktop-table {
    @include md-down {
      display: none;
    }
  }

  // Empty state in table
  .proposals-table-empty {
    padding: 2rem;
    text-align: center;
  }

  // Sentinel element for intersection observer
  .proposals-sentinel {
    height: 1px;
    width: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem 0;

    &__loading {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  // Mobile card styles
  .proposal-card {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 1rem;
    background: var(--white);
    border-bottom: 1px solid var(--slate-200);
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: var(--slate-50);
    }

    &:last-child {
      border-bottom: none;
    }

    &__left {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 0;
      flex: 1;
    }

    &__right {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.5rem;
      flex-shrink: 0;
      margin-left: 1rem;
    }

    &__primary {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__number {
      font-weight: 700;
      color: var(--slate-700);
    }

    &__title {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__secondary {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      color: var(--slate-500);
    }

    &__date {
      &::after {
        content: '·';
        margin-left: 0.5rem;
      }
    }

    &__contact {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__amount {
      font-weight: 600;
      font-size: 1rem;
      color: var(--slate-900);
    }

    &--skeleton {
      pointer-events: none;
      cursor: default;

      .skeleton {
        border-radius: 0.5rem;
      }
    }
  }

  .status-filter {
    .filter-active {
      background-color: var(--color-primary-50);
      border-color: var(--color-primary-200);
    }

    .filter-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      margin-left: 6px;
      font-size: 11px;
      font-weight: 600;
      color: var(--color-primary-700);
      background-color: var(--color-primary-100);
      border-radius: 9px;
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 4px 4px 8px;
      margin-bottom: 4px;
      font-size: 12px;
      font-weight: 600;
      color: var(--slate-600);
      border-bottom: 1px solid var(--slate-100);
    }

    &__reset {
      padding: 0;
      font-size: 11px;
      font-weight: 500;
      color: var(--color-primary-600);
      cursor: pointer;
      background: none;
      border: none;

      &:hover {
        color: var(--color-primary-700);
        text-decoration: underline;
      }
    }

    &__option {
      display: flex;
      gap: 8px;
      align-items: center;
      padding: 6px 4px;
      cursor: pointer;
      border-radius: 4px;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: var(--slate-50);
      }

      input[type='checkbox'] {
        width: 16px;
        height: 16px;
        cursor: pointer;
        accent-color: var(--color-primary-600);
      }
    }
  }

  .proposals-view__title-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
</style>
